import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import './ChartLayout.css';

Chart.register(...registerables);

export const SingleFamilyDetached = ({ location, geofips, preferredState = null, yearStart = 2010, yearEnd = 2023 }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationData, setLocationData] = useState([]);
  const [californiaData, setCaliforniaData] = useState([]);
  const [activeView, setActiveView] = useState('overview');

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!location || location === '') {
        console.log('No location set yet, waiting...');
        setLoading(false);
        return;
      }
      
      if (!geofips) {
        setError('A geofips is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching single_family_detached data for location:', location, 'geofips:', geofips);
        console.log('Year range:', yearStart, 'to', yearEnd);
        
        // Check if this is California state (geofips 6)
        const isCaliforniaState = geofips === 6 || (location && location.toLowerCase() === 'california');
        
        let locationDataResult = null;
        
        if (isCaliforniaState) {
          console.log('Detected California state - querying state data directly');
          const { data: stateData, error: stateErr } = await supabaseClient
            .from('single_family_detached')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geoid', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (stateErr) {
            console.error('Error fetching California state data:', stateErr);
            throw stateErr;
          }
          
          console.log('California state query result:', stateData?.length || 0, 'rows');
          locationDataResult = stateData || [];
        } else {
          // Try place (city) data first, then county
          let query = supabaseClient
            .from('single_family_detached')
            .select('*')
            .eq('geography_type', 'place')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (location) {
            console.log('Querying place data by location_name:', location);
            query = query.ilike('location_name', `%${location}%`);
          } else if (geofips) {
            console.log('Querying place data by geofips:', geofips);
            query = query.eq('geoid', geofips);
          }
          
          const { data: placeData, error: placeErr } = await query;
          
          if (placeErr) {
            console.error('Error fetching place data:', placeErr);
          }
          
          console.log('Place query result:', placeData?.length || 0, 'rows');
          if (placeData && placeData.length > 0) {
            console.log('Found place (city) data:', placeData.length, 'rows');
            locationDataResult = placeData;
          } else {
            console.log('No place data found, trying county data...');
            
            // Try county data
            let countyQuery = supabaseClient
              .from('single_family_detached')
              .select('*')
              .eq('geography_type', 'county')
              .gte('year', yearStart)
              .lte('year', yearEnd)
              .order('year', { ascending: true });
            
            if (geofips) {
              console.log('Querying county data by geoid:', geofips);
              countyQuery = countyQuery.eq('geoid', geofips);
            } else if (location) {
              console.log('Querying county data by location_name:', location);
              countyQuery = countyQuery.ilike('location_name', `%${location}%`);
            }
            
            const { data: countyData, error: countyErr } = await countyQuery;
            
            if (countyErr) {
              console.error('Error fetching county data:', countyErr);
            }
            
            console.log('County query result:', countyData?.length || 0, 'rows');
            locationDataResult = countyData || [];
          }
        }

        // Always fetch California state data for comparison (unless this IS California)
        let californiaDataResult = [];
        if (!isCaliforniaState) {
          const { data: caData, error: caErr } = await supabaseClient
            .from('single_family_detached')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geoid', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (caErr) {
            console.error('Error fetching California comparison data:', caErr);
          } else {
            console.log('California comparison data:', caData?.length || 0, 'rows');
            californiaDataResult = caData || [];
          }
        }

        console.log('Location data found:', locationDataResult?.length || 0, 'rows');
        if (locationDataResult && locationDataResult.length > 0) {
          console.log('Sample location data:', locationDataResult[0]);
        }
        
        setLocationData(locationDataResult || []);
        setCaliforniaData(californiaDataResult || []);
        setLoading(false);
      } catch (e) {
        console.error('Error loading single_family_detached:', e);
        setError(e.message || 'Failed to load single family detached data');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, yearStart, yearEnd, location]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || locationData.length === 0) return null;
    
    const yearsData = locationData.map(r => r.year).filter(y => !isNaN(y));
    if (yearsData.length === 0) return null;
    
    const latestYear = Math.max(...yearsData);
    const firstYear = Math.min(...yearsData);
    
    const latestRow = locationData.find(r => r.year === latestYear);
    const firstRow = locationData.find(r => r.year === firstYear);
    
    if (!latestRow || latestRow.percentage_single_family_detached === null || latestRow.percentage_single_family_detached === undefined) {
      return null;
    }
    
    const latestPercent = Number(latestRow.percentage_single_family_detached);
    
    let percentChange = null;
    if (firstRow && firstRow.percentage_single_family_detached !== null && firstRow.percentage_single_family_detached !== undefined) {
      const firstPercent = Number(firstRow.percentage_single_family_detached);
      percentChange = latestPercent - firstPercent;
    }
    
    if (isNaN(latestPercent)) {
      return null;
    }
    
    return {
      latestPercent: latestPercent,
      percentChange: percentChange,
      latestYear: latestYear,
      firstYear: firstYear
    };
  }, [loading, locationData]);

  // Build the chart after data loads
  useEffect(() => {
    if (loading || locationData.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    console.log('Building chart with locationData:', locationData);
    console.log('California data:', californiaData);

    // Prepare data for location
    const locationPercentData = years.map(year => {
      const row = locationData.find(r => r.year === year);
      const value = row && row.percentage_single_family_detached !== null ? row.percentage_single_family_detached : null;
      return value;
    });

    // Prepare data for California
    const californiaPercentData = years.map(year => {
      const row = californiaData.find(r => r.year === year);
      const value = row && row.percentage_single_family_detached !== null ? row.percentage_single_family_detached : null;
      return value;
    });

    console.log('Location percent data array:', locationPercentData);
    console.log('California percent data array:', californiaPercentData);

    const chartTitle = `Single-Family Detached Homes (%) - ${location || 'Selected Location'}`;

    const datasets = [
      {
        label: location || 'Selected Location',
        data: locationPercentData,
        borderColor: '#3b82f6', // Blue
        backgroundColor: '#3b82f633',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      }
    ];

    // Only add California line if we have data and this is not California
    if (californiaData.length > 0) {
      datasets.push({
        label: 'California',
        data: californiaPercentData,
        borderColor: '#ef4444', // Red
        backgroundColor: '#ef444433',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      });
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: 'Percent (%)' },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              callback: (v) => `${v}%`
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          title: {
            display: true,
            text: chartTitle,
            font: {
              size: 16,
              weight: 600
            },
            padding: {
              top: 10,
              bottom: 20
            }
          },
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 15,
              padding: 10,
              font: {
                size: 11
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toFixed(1) : 'N/A'}%`;
              }
            }
          }
        }
      }
    });
  }, [loading, years, locationData, californiaData, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Single_Family_Detached');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const rows = locationData.map(row => ({
      year: row.year,
      geoid: row.geoid,
      location_name: row.location_name,
      geography_type: row.geography_type,
      percentage_single_family_detached: row.percentage_single_family_detached,
      margin_of_error: row.margin_of_error
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'Single_Family_Detached_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading single family detached data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  if (!location) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        Please select a location to view Single Family Detached data.
      </div>
    );
  }
  
  if (!loading && locationData.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        No single family detached data available for {location}.
      </div>
    );
  }

  const chartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display
  const keyMetricsDisplay = keyMetrics && keyMetrics.latestPercent !== undefined ? (
    <KeyMetrics>
      <KeyMetric
        value={`${keyMetrics.latestPercent.toFixed(1)}%`}
        label={`Single-Family Detached (${keyMetrics.latestYear})`}
        color="#3b82f6"
      />
      {keyMetrics.percentChange !== null && (
        <KeyMetric
          value={`${keyMetrics.percentChange > 0 ? '+' : ''}${keyMetrics.percentChange.toFixed(1)}%`}
          label={`Change Since ${keyMetrics.firstYear}`}
          color={keyMetrics.percentChange < 0 ? '#10b981' : '#ef4444'}
        />
      )}
    </KeyMetrics>
  ) : null;

  // Generate dynamic key insights text
  const generateKeyInsights = () => {
    if (!keyMetrics) return 'Loading data...';
    
    const latestYear = keyMetrics.latestYear;
    const firstYear = keyMetrics.firstYear;
    const latestPercent = keyMetrics.latestPercent;
    const percentChange = keyMetrics.percentChange;
    
    // Check if this is California
    const isCaliforniaState = geofips === 6 || (location && location.toLowerCase() === 'california');
    
    // Get California's latest percentage if available (for comparison)
    let caPercent = null;
    if (!isCaliforniaState && californiaData.length > 0) {
      const caLatest = californiaData.find(r => r.year === latestYear);
      if (caLatest && caLatest.percentage_single_family_detached !== null) {
        caPercent = Number(caLatest.percentage_single_family_detached);
      }
    }
    
    // Determine sprawl level and trend
    let sprawlLevel = 'average sprawl';
    let sprawlTrend = percentChange < 0 ? 'decreasing' : 'increasing';
    
    // Simple heuristic: lower percentage = less sprawl
    if (latestPercent < 40) {
      sprawlLevel = 'less sprawl than average';
    } else if (latestPercent > 60) {
      sprawlLevel = 'more sprawl than average';
    }
    
    let insightText = '';
    
    if (isCaliforniaState) {
      // California-specific text without comparison
      insightText = `As measured by home structures, California has ${sprawlLevel} and sprawl is ${sprawlTrend}. `;
      insightText += `${latestPercent.toFixed(0)}% of homes were single-family detached in ${latestYear}`;
      
      if (percentChange !== null) {
        const direction = percentChange < 0 ? 'down' : 'up';
        const firstPercent = latestPercent - percentChange;
        insightText += ` (${direction} from ${firstPercent.toFixed(0)}% in ${firstYear})`;
      }
      insightText += '.';
    } else {
      // Non-California locations with comparison
      insightText = `As measured by home structures, ${location} has ${sprawlLevel} and sprawl is ${sprawlTrend}. `;
      insightText += `${latestPercent.toFixed(0)}% of homes were single-family detached in ${latestYear}`;
      
      if (percentChange !== null) {
        const direction = percentChange < 0 ? 'down' : 'up';
        const firstPercent = latestPercent - percentChange;
        insightText += ` (${direction} from ${firstPercent.toFixed(0)}% in ${firstYear})`;
      }
      
      if (caPercent !== null) {
        insightText += ` compared to the statewide average of ${caPercent.toFixed(0)}%`;
      }
      insightText += '.';
    }
    
    return insightText;
  };

  return (
    <ChartLayout
      title="Single-Family Detached Homes"
      key={`single-family-detached-${location}`}
      description={`${location} • Source: American Community Survey (ACS) 5-Year Estimates`}
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Single_Family_Detached')}
      csvFilename={formatFilename(location, 'Single_Family_Detached_Data')}
      enableComments={true}
      location={location}
      overviewContent={
        <OverviewContent
          title="Percentage Single-Family Detached Homes"
          description="This chart shows the percentage of housing units that are single-family detached homes over time."
        >
          <div>
            <p>{generateKeyInsights()}</p>
          </div>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Policy Relevance"
          description="Single-family detached housing produces higher energy use, land consumption, and car dependence."
        >
          <div>
            <p>Larger homes and sprawling neighborhoods increase emissions and infrastructure demands. Reforming zoning, improving energy efficiency, and promoting infill development can reduce impacts and expand housing options.</p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="Homeownership in the United States has been used to build generational wealth, a benefit that was systematically denied to many communities of color due to historical discriminatory and racist land use and lending practices."
        >
          <div>
            <p>Today, homeownership remains strongly correlated with educational attainment, access to quality jobs, health, energy burden, and adaptive capacity. Increasing equitable access to affordable housing is key to creating more resilient and sustainable communities.</p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="Data are from the American Community Survey 5-Year sample, with the last year of the 5-year sample representing the current year. The US Census conducts a rolling sample of U.S. households every year."
          dataSources={[
            'American Community Survey (ACS) 5-Year Estimates',
            'Housing units by structure type',
            'US Census rolling household sample'
          ]}
          methodology={[
            'Percentage single-family detached is derived from total number of homes per type of structure',
            'Other structure types are mostly multi-family, but also include mobile homes, condominiums, and other attached structures',
            'Data filtered by geography type (place, county, or state) and location',
            'Percentile rankings calculated against all locations in the dataset'
          ]}
        />
      }
    />
  );
};

export default SingleFamilyDetached;

